import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { S25PipesModule } from "../../../../../pipes/s25.pipes.module";
import { S25ButtonComponent } from "../../../../../standalone/s25.button.component";
import { S25DatepickerModule } from "../../../../s25-datepicker/s25.datepicker.module";
import { S25ToggleButtonModule } from "../../../../s25-toggle-button/s25.toggle.button.module";
import { S25InfoMessageModule } from "../../../../s25-info-message/s25.info.message.module";
import { invoiceDisplayName, PricingStore, PricingView } from "../../s25.event.pricing.store";
import { isDefaultSet } from "../system-sets/system-sets";
import { S25Util } from "../../../../../util/s25-util";
import { FormsModule } from "@angular/forms";
import { S25PricingRateGroupDropdown } from "../../pricing-rate-group-dropdown/s25.pricing.rate.group.dropdown";
import { S25PricingPrintInvoiceComponent } from "../../print-invoice/s25.pricing.print.invoice.component";
import { S25LoadingInlineStaticComponent } from "../../../../s25-loading/s25.loading.inline.static.component";
import { EventService } from "../../../../../services/event.service";
import { Bind } from "../../../../../decorators/bind.decorator";
import { S25Datefilter } from "../../../../s25-dateformat/s25.datefilter.service";
import { S25DateParse } from "../../../../../util/core/s25.date.parse";
import { WSReservations } from "../../../../s25-event/EventMicroI";
import { Proto } from "../../../../../pojo/Proto";
import { S25IncludedOccurrencesComponent } from "../../included-occurrences/s25.included.occurrences.component";
import { S25FadingSuccessMsgComponent } from "../../../../s25-fading-success-msg/s25.fading.success.msg.component";
import ISODateString = Proto.ISODateString;

@TypeManagerDecorator("s25-ng-invoice-control-panel")
@Component({
    selector: "s25-ng-invoice-control-panel",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25PipesModule,
        S25ButtonComponent,
        S25DatepickerModule,
        S25ToggleButtonModule,
        S25InfoMessageModule,
        FormsModule,
        S25PricingRateGroupDropdown,
        S25PricingPrintInvoiceComponent,
        S25LoadingInlineStaticComponent,
        S25IncludedOccurrencesComponent,
        S25FadingSuccessMsgComponent,
    ],
    template: `
        @if (selectedInvoice()) {
            <section class="ic-panel">
                <div class="ic-head">
                    <div class="ic-id">
                        <h2 class="ic-name">
                            {{ invoiceName() }}
                            @if (loading()) {
                                <s25-ng-loading-inline-static [text]="null" />
                            }
                        </h2>
                        <div class="ic-sub">{{ summaryLine() }}</div>
                    </div>
                    <div class="ic-actions">
                        @if (confirmingDelete()) {
                            <p class="ic-delete-confirm">Delete {{ invoiceName() }}?</p>
                            <s25-ng-button [type]="'danger'" [onClick]="confirmDelete">Confirm Delete</s25-ng-button>
                            <button type="button" class="aw-button aw-button--outline" (click)="cancelDelete()">
                                Cancel
                            </button>
                        } @else if (canEdit()) {
                            @if (!invoiceLocked()) {
                                <s25-ng-button [type]="'outline'" [onClick]="refreshEventPricing">
                                    Refresh Pricing
                                </s25-ng-button>
                            }

                            <button type="button" class="aw-button aw-button--outline" (click)="createInvoice()">
                                Create Invoice
                            </button>

                            @if (!invoiceLocked()) {
                                <button type="button" class="aw-button aw-button--outline" (click)="editInvoice()">
                                    Edit Invoice
                                </button>

                                @if (!deleteDisabled()) {
                                    <s25-ng-button [type]="'danger--outline'" (clicked)="promptDelete()">
                                        Delete Invoice
                                    </s25-ng-button>
                                }
                            }
                        }
                    </div>
                </div>

                @if (error()) {
                    <p class="cn-alert cn-alert--danger" role="alert">{{ error() }}</p>
                }

                @if (successMsg()) {
                    <s25-ng-fading-success-msg [(message)]="successMsg" />
                }

                @if (eventsNeedingRefresh().length) {
                    <div class="cn-alert cn-alert--warning">
                        <div>
                            <p class="ngBold">Pricing is out of date</p>
                            <p>
                                {{ refreshWarning() }}
                            </p>
                        </div>
                        <s25-ng-button [type]="'primary'" [onClick]="refreshEventPricing"
                            >Refresh Pricing</s25-ng-button
                        >
                    </div>
                }

                <div class="ic-rule" aria-hidden="true"></div>

                <div class="ic-toolbar">
                    @if (hasOccurrences()) {
                        <div class="ic-field">
                            <span class="ic-label" aria-hidden="true">View</span>
                            <div class="ic-view-toggle" role="group" aria-label="Table view">
                                <button
                                    type="button"
                                    [class]="isOccurrenceView() ? 'c-textButton' : 'aw-button aw-button--primary'"
                                    [attr.aria-pressed]="!isOccurrenceView()"
                                    (click)="setPricingView('lineItem')"
                                >
                                    Line Item
                                </button>
                                <button
                                    type="button"
                                    [class]="isOccurrenceView() ? 'aw-button aw-button--primary' : 'c-textButton'"
                                    [attr.aria-pressed]="isOccurrenceView()"
                                    (click)="setPricingView('occurrence')"
                                >
                                    Occurrence
                                </button>
                            </div>
                        </div>

                        <div class="ic-divider" aria-hidden="true"></div>
                    }

                    <div class="ic-field">
                        <span class="ic-label" aria-hidden="true">Pricing Date</span>
                        @if (canEdit() && !invoiceLocked()) {
                            <s25-datepicker
                                [(value)]="date"
                                [showToday]="true"
                                [ariaLabel]="'Pricing Date'"
                                (valueChange)="updateDate()"
                            />
                        } @else {
                            <p class="ic-value">{{ date() | dateFormat: dateFormat() }}</p>
                        }
                    </div>

                    <div class="ic-field">
                        <span class="ic-label">Included Occurrences</span>
                        <div class="ic-occ-value">
                            <span class="ic-occ-count">{{ occurrenceCountDisplay() }}</span>
                            @if (occurrenceDateRangeDisplay()) {
                                <span class="ic-occ-date">· {{ occurrenceDateRangeDisplay() }}</span>
                            }
                            @if (canEdit() && !invoiceLocked()) {
                                <button
                                    type="button"
                                    class="c-textButton"
                                    [attr.aria-expanded]="occEditing()"
                                    [attr.aria-label]="
                                        occEditing() ? 'Hide included occurrences' : 'Show included occurrences'
                                    "
                                    (click)="occEditing.set(!occEditing())"
                                >
                                    {{ occEditing() ? "Hide" : "Show" }}
                                </button>
                            }
                        </div>
                    </div>

                    <div class="ic-divider" aria-hidden="true"></div>

                    @if (canEdit()) {
                        <div class="ic-field ic-field--lock">
                            <div class="ic-lock-row">
                                <span class="ic-label" aria-hidden="true">Invoice Lock</span>
                                <s25-ng-info-message [isPopover]="true">
                                    {{ invoiceLockInfoMsg }}
                                </s25-ng-info-message>
                            </div>
                            <s25-toggle-button
                                [value]="invoiceLocked()"
                                [falseLabel]="'Off'"
                                [trueLabel]="'On'"
                                [toggleLabel]="'Invoice Lock'"
                                (valueChange)="toggleLockInvoice($event)"
                            />
                        </div>
                    }

                    @if (!invoiceLocked()) {
                        <div class="ic-field">
                            <span class="ic-label" aria-hidden="true">Update All Items to Rate Group</span>
                            <s25-ng-pricing-rate-group-dropdown [(panelLoading)]="loading" />
                        </div>
                    }

                    <div class="ic-divider" aria-hidden="true"></div>

                    <div class="ic-field">
                        <span class="ic-label" aria-hidden="true">Print Invoice</span>
                        <s25-ng-pricing-print-invoice />
                    </div>
                </div>
            </section>

            @if (occEditing()) {
                <section class="ic-panel ic-occ-editor">
                    <s25-ng-included-occurrences />
                </section>
            }
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .ic-panel {
            border-radius: 3px;
            box-shadow:
                rgba(0, 0, 0, 0.05) 0 6px 24px 0,
                rgba(0, 0, 0, 0.08) 0 0 0 1px;
        }

        .ic-panel + .ic-panel {
            margin-top: 1.5rem;
        }

        .ic-head {
            display: flex;
            align-items: flex-start;
            gap: 20px;
            padding: 20px 22px;
        }

        .cn-alert.cn-alert--warning p,
        .cn-alert.cn-alert--danger {
            font-size: 0.9rem;
        }

        .cn-alert.cn-alert--warning s25-ng-button {
            margin-left: auto;
        }

        .cn-alert.cn-alert--danger {
            margin-inline: 22px;
        }

        .ic-id {
            min-width: 0;
        }

        .ic-name {
            margin: 0;
            font-size: 1.75rem;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .ic-name s25-ng-loading-inline-static {
            display: flex;
            align-items: center;
        }

        :host .ic-name s25-ng-loading-inline-static ::ng-deep .c-loading {
            width: 20px;
            height: 20px;
        }

        .ic-sub {
            font-size: 14px;
            margin-top: 4px;
            color: var(--color--text-muted);
        }

        .ic-occ-value {
            display: flex;
            align-items: baseline;
            gap: 6px;
        }

        .ic-occ-count {
            font-size: 1rem;
        }

        .ic-occ-date {
            font-size: 14px;
            color: var(--color--text-muted);
        }

        .ic-actions {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: flex-end;
        }

        .ic-delete-confirm {
            margin: 0;
            font-size: 0.9rem;
            font-weight: 600;
        }

        .ic-rule {
            height: 1px;
            background: var(--color--border);
        }

        .ic-toolbar {
            display: flex;
            align-items: flex-start;
            gap: 20px;
            padding: 16px 22px;
            flex-wrap: wrap;
        }

        .ic-field {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        .ic-field--lock {
            gap: 0;
        }

        .ic-view-toggle {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .ic-field--lock .ic-lock-row {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .ic-label {
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .ic-value {
            display: flex;
            align-items: center;
            min-width: 150px;
            margin: 0;
        }

        .ic-divider {
            width: 1px;
            align-self: stretch;
            background: var(--color--border);
            margin: 0 2px;
        }

        /* Mobile */
        @media (max-width: 640px) {
            .ic-head {
                flex-direction: column;
                gap: 14px;
                padding: 16px;
            }

            .ic-actions {
                margin-left: 0;
                width: 100%;
            }

            .ic-actions .aw-button,
            .ic-actions s25-ng-button {
                flex: 1;
            }

            .ic-toolbar {
                flex-direction: column;
                align-items: stretch;
                gap: 16px;
                padding: 16px;
            }

            .ic-field {
                width: 100%;
            }

            .ic-divider {
                display: none;
            }
        }
    `,
})
export class S25InvoiceControlPanelComponent {
    private readonly pricingStore = inject(PricingStore);

    readonly selectedInvoice = this.pricingStore.selectedInvoice;
    readonly invoiceId = computed(() => this.selectedInvoice()?.id ?? 0);
    readonly invoiceName = computed(() => invoiceDisplayName(this.selectedInvoice()));
    readonly invoiceLocked = computed(() => !!this.selectedInvoice()?.billDefn?.completed);
    readonly eventsNeedingRefresh = this.pricingStore.eventsNeedingRefresh;

    readonly occurrenceIds = this.pricingStore.selectedInvoiceOccIds;

    protected readonly hasOccurrences = this.pricingStore.selectedInvoiceHasOccurrences;
    protected readonly isOccurrenceView = this.pricingStore.isOccurrenceView;

    protected setPricingView(view: PricingView) {
        this.pricingStore.setPricingView(view);
    }

    protected createInvoice() {
        this.pricingStore.openInvoiceForm({ mode: "create" });
    }

    protected editInvoice() {
        const invoiceId = this.invoiceId();
        if (!invoiceId) return;
        this.pricingStore.openInvoiceForm({ mode: "edit", invoiceId });
    }

    private readonly occurrences = this.pricingStore.occurrences;

    readonly occurrenceDateRange = computed(() => {
        const occMap: Record<number, WSReservations> = this.occurrences();
        const ids = this.occurrenceIds();
        if (!ids.size) return undefined;

        let minStart: Date | undefined;
        let maxEnd: Date | undefined;

        for (const rsrvId of ids) {
            const occ = occMap[rsrvId];
            if (!occ) continue;

            const start = S25DateParse.parseDropTZLenient(<ISODateString>occ.evStartDt);
            const end = S25DateParse.parseDropTZLenient(<ISODateString>occ.evEndDt);

            if (!minStart || start < minStart) minStart = start;
            if (!maxEnd || end > maxEnd) maxEnd = end;
        }

        if (!minStart || !maxEnd) return undefined;
        return { start: minStart, end: maxEnd };
    });

    protected readonly occurrenceCountDisplay = computed(() => {
        const count = this.occurrenceIds().size;
        return `${count} ${S25Util.pluralize("occurrence", count)}`;
    });

    protected readonly occurrenceDateRangeDisplay = computed(() => {
        const range = this.occurrenceDateRange();
        const format = this.dateFormat();
        if (!range || !format) return "";

        const startStr = S25Datefilter.transform(range.start, format);
        const endStr = S25Datefilter.transform(range.end, format);

        return startStr === endStr ? startStr : `${startStr} – ${endStr}`;
    });

    readonly combineRelatedEvents = computed(() => this.pricingStore.combineRelated());
    readonly canEdit = computed(() => this.pricingStore.canEditPricing());
    date = linkedSignal(() => {
        const date = this.selectedInvoice()?.billDefn?.billDate;
        return new Date(S25Util.date.dropTZString(date));
    });
    readonly dateFormat = computed(() => this.pricingStore.dateFormats()?.date);
    // Nothing to delete on the default set: it is where billing sits when no invoice claims it.
    protected readonly deleteDisabled = computed(
        () => !!this.selectedInvoice()?.payments?.length || isDefaultSet(this.selectedInvoice()),
    );

    /** Dropped whenever the panel moves to another invoice, so no prompt outlives what it asked about. */
    protected readonly confirmingDelete = linkedSignal<number, boolean>({
        source: this.invoiceId,
        computation: () => false,
    });

    readonly occEditing = signal(false);
    loading = linkedSignal(() => this.pricingStore.invoicesLoading());
    error = linkedSignal(() => this.pricingStore.invoiceError()?.message ?? "");
    successMsg = signal("");

    protected readonly invoiceLockInfoMsg =
        'Invoice lock prevents adjustments, adding/removing occurrences, formula updates, reflection of reservation changes to associated occurrences, and changing the "Charge To" organization or Rate Group.';
    protected readonly refreshWarning = computed(() => {
        const numEventsNeedingRefresh = this.eventsNeedingRefresh()?.length ?? 0;
        return `${numEventsNeedingRefresh} ${S25Util.pluralize("event", numEventsNeedingRefresh)} ha${numEventsNeedingRefresh === 1 ? "s" : "ve"} changed since this invoice was last priced. Refresh to update line items.`;
    });

    readonly summaryLine = computed(() => {
        const invoice = this.selectedInvoice();
        if (!invoice) return "";
        const lineItemCount = invoice.billing?.lineItems?.length ?? 0;
        const orgCount = new Set(invoice.billing?.lineItems?.map((item) => item.chargeToId) ?? []).size;
        return `${orgCount} ${S25Util.pluralize("organization", orgCount)} · ${lineItemCount} line ${S25Util.pluralize("item", lineItemCount)}`;
    });

    async updateDate() {
        if (!this.date()) return;
        this.toggleLoading();
        this.error.set("");
        const { error } = await this.pricingStore.updateInvoice(
            { billDate: S25Util.date.toS25ISODateStrEndOfDay(S25Util.date.getDate(this.date())) },
            this.invoiceId(),
        );
        this.toggleLoading();
        if (error) this.error.set(error);
    }

    async toggleLockInvoice(locked: boolean | undefined) {
        if (!this.invoiceId()) return;
        this.toggleLoading();
        this.error.set("");
        const { error } = await this.pricingStore.updateInvoice({ completed: !!locked }, this.invoiceId());
        this.toggleLoading();
        if (error) this.error.set(error);
    }

    toggleLoading() {
        this.loading.update((prev) => !prev);
    }

    protected promptDelete() {
        this.error.set("");
        this.confirmingDelete.set(true);
    }

    protected cancelDelete() {
        this.confirmingDelete.set(false);
    }

    @Bind
    protected async confirmDelete() {
        const invoiceId = this.invoiceId();
        if (!invoiceId) return;
        this.toggleLoading();
        this.error.set("");
        const { error } = await this.pricingStore.deleteInvoice(invoiceId);
        this.toggleLoading();
        this.confirmingDelete.set(false);
        if (error) return this.error.set(error);
        this.successMsg.set("Invoice deleted");
    }

    @Bind
    async refreshEventPricing() {
        // Nothing flagged as stale still means "reprice what I am looking at"
        const flaggedIds: number[] = this.eventsNeedingRefresh().map((event: { event_id: number }) => event.event_id);
        const eventIds = flaggedIds.length ? flaggedIds : this.pricingStore.eventIds();
        if (!eventIds.length) return;
        this.error.set("");
        try {
            await Promise.all(eventIds.map((eventId) => EventService.updateEventPricing(eventId)));
            this.pricingStore.reloadEventPricing();
            await this.pricingStore.reloadInvoices();
            this.successMsg.set("Pricing refreshed");
        } catch (caughtError) {
            this.error.set(S25Util.errorText(caughtError, "Something went wrong. Please try again later."));
        }
    }
}
